// apps/dashboard/src/components/layout/sidebar.tsx
import { OnboardingChecklist } from '@/components/OnboardingChecklist';
import { useBranding } from '@/hooks/use-branding';
import { useMentionCount } from '@/hooks/use-mentions';
import { cn } from '@/lib/cn';
import { useAuthStore } from '@/stores/auth.store';
import { Avatar, Popover, PopoverContent, PopoverTrigger, Separator } from '@ominiflow/ui';
import { Link } from '@tanstack/react-router';
import {
  Activity,
  BarChart3,
  Bot,
  Box,
  CreditCard,
  Filter,
  Inbox,
  LayoutGrid,
  LogOut,
  Megaphone,
  MessageSquare,
  MoreVertical,
  PenSquare,
  Settings,
  Store,
  TrendingUp,
  Users,
  Zap,
} from 'lucide-react';
import type React from 'react';
import { useState } from 'react';
import { VersionBadge, VersionInfo } from './version-badge';

const NAV_SECTIONS = [
  {
    items: [
      { to: '/inbox', icon: Inbox, label: 'Inbox' },
      { to: '/contacts', icon: Users, label: 'Contatos' },
      { to: '/pipeline', icon: LayoutGrid, label: 'Funil de Vendas' },
      { to: '/segments', icon: Filter, label: 'Segmentos' },
      { to: '/automations/tags', icon: Zap, label: 'Automações de Tag' },
      { to: '/campaigns', icon: Megaphone, label: 'Campanhas' },
      { to: '/settings/channels/social-posts', icon: PenSquare, label: 'Posts Meta' },
      { to: '/templates', icon: MessageSquare, label: 'Templates' },
    ],
  },
  {
    label: 'Automação',
    items: [
      { to: '/automation', icon: Bot, label: 'Automação' },
      { to: '/settings/macros', icon: Zap, label: 'Macros' },
      { to: '/bot-analytics', icon: TrendingUp, label: 'Bot Analytics' },
      { to: '/automation/wa-flows', icon: Zap, label: 'WA Flows' },
    ],
  },
  {
    label: 'Inteligência',
    items: [
      { to: '/analytics', icon: BarChart3, label: 'Analytics' },
      { to: '/live-view', icon: Activity, label: 'Live View' },
    ],
  },
  {
    items: [
      { to: '/marketplace', icon: Store, label: 'Marketplace' },
      { to: '/billing', icon: CreditCard, label: 'Plano' },
      { to: '/settings/agents', icon: Users, label: 'Equipe' },
      { to: '/settings', icon: Settings, label: 'Configurações' },
    ],
  },
] satisfies Array<{
  label?: string;
  items: Array<{ to: string; icon: React.ComponentType<{ className?: string }>; label: string }>;
}>;

const RESELLER_NAV_ITEMS = [{ to: '/reseller/hub', icon: Box, label: 'Reseller Hub' }] as const;

interface SidebarProps {
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

export function Sidebar({ collapsed = false }: SidebarProps) {
  const { data: brandingData } = useBranding();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const [hovered, setHovered] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const mentionCount = useMentionCount();

  // Expand on hover when pinned-collapsed
  const isExpanded = !collapsed || hovered;

  function handleLogout() {
    logout();
    window.location.replace('/login');
  }
  const logoUrl = brandingData?.brandingConfig?.logoUrl;
  const removeOmniflowBranding = brandingData?.brandingConfig?.removeOmniflowBranding;

  return (
    <aside
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={cn(
        'flex flex-col border-r border-border bg-card transition-all duration-200 ease-in-out shrink-0',
        isExpanded ? 'w-56' : 'w-14',
      )}
    >
      {/* Brand logo — uses tenant logoUrl when white-label is configured */}
      <div
        className={cn(
          'flex h-14 items-center border-b border-border shrink-0',
          isExpanded ? 'px-4 gap-3' : 'justify-center px-0',
        )}
      >
        {logoUrl ? (
          <img
            src={logoUrl}
            alt="OmniFlow Logo"
            className={cn('object-contain', isExpanded ? 'h-8 max-w-28' : 'h-8 w-8')}
          />
        ) : (
          <>
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl gradient-brand shadow-sm">
              <Zap className="h-4 w-4 text-white" strokeWidth={2.5} />
            </div>
            {isExpanded && !removeOmniflowBranding && (
              <span className="font-bold text-base gradient-brand-text tracking-tight">
                OmniFlow
              </span>
            )}
          </>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex flex-col gap-0.5 p-2 flex-1 overflow-y-auto">
        {user?.isReseller &&
          RESELLER_NAV_ITEMS.map(({ to, icon: Icon, label }) => (
            <Link
              key={to}
              to={to as any}
              title={!isExpanded ? label : undefined}
              className={cn(
                'group relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-semibold mb-2',
                'bg-primary/5 text-primary hover:bg-primary/10 transition-all duration-150',
                '[&.active]:bg-linear-to-r [&.active]:from-primary/15 [&.active]:to-primary/5',
              )}
            >
              <Icon className="h-4 w-4 shrink-0" strokeWidth={1.5} />
              {isExpanded && <span>{label}</span>}
            </Link>
          ))}

        {NAV_SECTIONS.map((section, si) => (
          <div key={section.label ?? section.items[0].to} className={cn(si > 0 && 'mt-1.5')}>
            {/* Section label (expanded only) */}
            {isExpanded && section.label && (
              <p className="px-3 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/40 select-none">
                {section.label}
              </p>
            )}
            {/* Divider (collapsed only, between sections) */}
            {!isExpanded && section.label && si > 0 && (
              <div className="my-1.5 mx-2 h-px bg-border/50" />
            )}

            {section.items.map(({ to, icon: Icon, label }) => (
              <Link
                key={to}
                to={to as any}
                aria-label={!isExpanded ? label : undefined}
                title={!isExpanded ? label : undefined}
                className={cn(
                  'relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium',
                  'text-muted-foreground hover:bg-accent/70 hover:text-foreground',
                  'transition-all duration-150',
                  // Gradient pill active state (H1-06 T3)
                  '[&.active]:bg-linear-to-r [&.active]:from-primary/15 [&.active]:to-primary/5',
                  '[&.active]:text-primary [&.active]:font-semibold',
                  '[&.active]:shadow-[inset_2px_0_0_hsl(var(--color-primary,220_90%_56%))]',
                )}
              >
                <span className="relative shrink-0">
                  <Icon className="h-4 w-4" strokeWidth={1.5} />
                  {to === '/inbox' && mentionCount > 0 && (
                    <span className="absolute -top-1 -right-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-primary text-[9px] font-bold text-primary-foreground leading-none">
                      {mentionCount > 9 ? '9+' : mentionCount}
                    </span>
                  )}
                </span>
                {isExpanded && <span>{label}</span>}
              </Link>
            ))}
          </div>
        ))}
      </nav>

      {/* Onboarding checklist — shown above footer when incomplete and not dismissed */}
      {isExpanded && <OnboardingChecklist collapsed={false} />}

      {/* User footer — avatar + presence + menu (H1-06 T3) */}
      <div className="border-t border-border p-2 mt-auto shrink-0">
        <Popover open={userMenuOpen} onOpenChange={setUserMenuOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label="Abrir menu do usuário"
              className={cn(
                'flex w-full items-center gap-2.5 rounded-lg px-2 py-2',
                'hover:bg-accent/70 transition-colors duration-150 cursor-pointer',
                isExpanded ? '' : 'justify-center',
              )}
            >
              {/* Avatar with presence dot */}
              <div className="relative shrink-0">
                <Avatar name={user?.email ?? 'U'} size="sm" />
                <span className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full border-2 border-card bg-emerald-500" />
              </div>
              {isExpanded && (
                <>
                  <div className="flex-1 min-w-0 text-left">
                    <p className="text-xs font-medium text-foreground truncate">
                      {user?.email?.split('@')[0] ?? 'Agente'}
                    </p>
                    <p className="text-[10px] text-muted-foreground/60 truncate">
                      {user?.tenantName ?? 'Workspace'}
                    </p>
                  </div>
                  <MoreVertical className="h-3.5 w-3.5 text-muted-foreground/50 shrink-0" />
                </>
              )}
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" side="top" sideOffset={6} className="w-56 p-1.5">
            <div className="px-2 py-1.5 mb-1">
              <p className="text-xs font-semibold text-foreground truncate">{user?.email}</p>
              <p className="text-[11px] text-muted-foreground truncate">
                {user?.tenantName ?? 'Workspace'}
              </p>
            </div>
            <Separator className="my-1" />
            <Link
              to={'/settings/profile' as any}
              onClick={() => setUserMenuOpen(false)}
              className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-foreground hover:bg-accent/70 transition-colors w-full"
            >
              <Settings className="h-3.5 w-3.5 text-muted-foreground" />
              Configurações
            </Link>
            <Separator className="my-1" />
            <button
              type="button"
              onClick={handleLogout}
              className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-destructive hover:bg-destructive/10 transition-colors w-full"
            >
              <LogOut className="h-3.5 w-3.5" />
              Sair
            </button>
            <VersionInfo />
          </PopoverContent>
        </Popover>
      </div>

      {/* Versão compacta — sempre visível na base do sidebar */}
      <div className="px-3 py-1.5 flex justify-center">
        <VersionBadge
          feOnly={false}
          className={isExpanded ? '' : 'flex-col items-center gap-0.5'}
        />
      </div>
    </aside>
  );
}
